import {
  CombinedGraphQLErrors,
  defaultDataIdFromObject,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import type { Reference, StoreObject, StoreValue } from '@apollo/client';
import type { ReadFieldOptions } from '@apollo/client/cache';
import { ApolloLink } from '@apollo/client/link';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';
import { Sentry } from '@opendoor/observability';
import { GraphQLError } from 'graphql';
import type { FragmentDefinitionNode, GraphQLFormattedError, SelectionSetNode } from 'graphql';
import { throwError } from 'rxjs';
import { hasValue } from 'shared/helpers/globals';

import { getGrpcExtensions, toGraphQLError, UnauthenticatedError } from './Error';
import { getLoginUrl } from './loginRedirect';
import navigationHelpers from './navigationHelpers';
import { globalTrackError, globalTrackEvent } from './observability';

export type IgnorableError = {
  value: string;
};

/**
 * Sanitizes error messages by replacing unique identifiers with placeholders.
 * This prevents high cardinality in Datadog metrics while preserving error patterns.
 *
 * Replaces:
 * - UUIDs (e.g., 77f09452-8e96-4e69-993e-f56b0ff0281d → <UUID>)
 * - Numeric IDs after common patterns (e.g., "lead id: 12345" → "lead id: <ID>")
 * - Email addresses (e.g., user@example.com → <EMAIL>)
 * @internal
 */
export const sanitizeErrorMessage = (message: string): string => {
  return message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<UUID>')
    .replace(
      /\b(?:id|uuid|lead_id|seller_lead_uuid|address_uuid|customer_uuid|offer_uuid):\s*[0-9a-f-]+/gi,
      (match) => match.replace(/:\s*[0-9a-f-]+/i, ': <ID>'),
    )
    .replace(/\b[0-9]{6,}\b/g, '<ID>')
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '<EMAIL>');
};

/**
 * Extracts `filePath:lineNumber` from the first frame of a V8 stack trace string.
 * Returns null when the stack is absent or unparseable.
 * @internal
 */
export const extractStackLocation = (stack: string | undefined): string | null => {
  if (!stack) return null;
  for (const line of stack.split('\n')) {
    // V8 format: "    at functionName (path:line:col)" or "    at path:line:col"
    const match = line.match(/\((.+):(\d+):\d+\)/) ?? line.match(/^\s+at\s+(.+):(\d+):\d+$/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
  }
  return null;
};

const shouldIgnoreError = (
  ignorableErrorsList: IgnorableError[] | undefined,
  errorMessage: string,
): IgnorableError | undefined =>
  ignorableErrorsList
    ? ignorableErrorsList.find(
        (error) =>
          errorMessage.indexOf(error.value) !== -1 || error.value.indexOf(errorMessage) !== -1,
      )
    : undefined;

type OnErrorArgs = {
  // handler to customize posting to Sentry, if present errorReporterLink will not post to sentry - useful in combination with observability library
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is a type from apollo-client
  trackError?: (label: string, error: Error, extras?: { [k: string]: any }) => void;
  ignorableErrorsList?: Array<IgnorableError>;
};

const REDACTED_KEYS = ['password', 'currentPassword', 'newPassword'];

// Make sure we don't send sensitive data to tracking services
const redactVariables = (variables: Record<string, unknown>): Record<string, unknown> => {
  const copy = { ...variables };
  REDACTED_KEYS.forEach((key) => {
    if (copy[key]) {
      copy[key] = '[REDACTED]';
    }
  });
  return copy;
};

// https://www.apollographql.com/docs/react/data/error-handling/
export const errorReporterLink = ({ trackError, ignorableErrorsList }: OnErrorArgs) => {
  // Centralizes trackError/Sentry routing so the two error branches don't duplicate it.
  const report = (sanitizedMessage: string, err: Error, extras: Record<string, unknown>) => {
    if (trackError) {
      // Use sanitized message for the label (becomes Datadog tag) to prevent high cardinality
      trackError(sanitizedMessage, err, extras);
    } else {
      Sentry.withScope((scope) => {
        scope.setTag('source', 'Apollo errorReporterLink');
        scope.setExtras(extras);
        scope.setFingerprint([String(extras.operationName), sanitizedMessage]);
        Sentry.captureException(err);
      });
    }
  };

  return new ErrorLink(({ error, operation }) => {
    // generated in the chain, does not need reporting
    if (error instanceof UnauthenticatedError) {
      return;
    }

    const context = operation.getContext();
    const stackTrace = context.getStackTrace?.() ?? context.stackTrace;
    const operationName = operation.operationName ?? 'anonymous';
    const commonExtras = {
      operationName,
      variables: redactVariables(operation.variables),
      team: context.linkContext?.team,
    };

    // the result of this will be used as the error's fingerprint in sentry
    // errors with the same fingerprint are grouped together
    const stackLocation = extractStackLocation(stackTrace);
    const makeLabel = (message: string): string => {
      const base = `[${operationName}] ${sanitizeErrorMessage(message)}`;
      return stackLocation ? `${base}\n${stackLocation}` : base;
    };

    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((graphQLError) => {
        const { message, path } = graphQLError;

        if (shouldIgnoreError(ignorableErrorsList, message)) {
          return;
        }

        const label = makeLabel(message);
        const err = new GraphQLError(label, { path });
        err.stack = stackTrace ?? error.stack;

        report(label, err, { ...commonExtras, message, path });
      });
    } else {
      const { message } = error;

      if (shouldIgnoreError(ignorableErrorsList, message)) {
        return;
      }

      const label = makeLabel(message);
      const err = new Error(label);
      err.stack = stackTrace ?? error.stack;

      const extras: Record<string, unknown> = { ...commonExtras, message };
      if (ServerError.is(error) || ServerParseError.is(error)) {
        extras.statusCode = error.statusCode;
      }

      report(label, err, extras);
    }
  });
};

// batches by default unless specifically specified otherwise (by adding `batch: false` to context)
export const batchSplitterLink = (uri: string) =>
  ApolloLink.split(
    (operation) => operation.getContext().batch ?? true,
    new BatchHttpLink({
      uri,
      credentials: 'include',
      headers: {
        batch: 'true',
      },
    }),
    new HttpLink({ uri, credentials: 'include' }),
  );

type RetryLinkParams = { initial?: number; max?: number };

export const retryLink = (params?: RetryLinkParams) =>
  new RetryLink({
    delay: {
      initial: params?.initial ?? 500,
      max: params?.max ?? 3000, // 3 seconds
      jitter: true,
    },
    attempts: {
      max: 5,
      // Skip retries if the operation has `retry: false` in its context
      retryIf: (error, operation) => !!error && (operation.getContext().retry ?? true),
    },
  });

// by default RetryLink only handles "network" errors. This makes it handle all errors.
// See: https://github.com/apollographql/apollo-link/issues/541
export const raiseRetryableGQLErrorsLink = (blockedRetryListStatusNumbers?: Array<number>) => {
  const shouldRetry = (error: GraphQLFormattedError): boolean => {
    const ext = getGrpcExtensions(error);
    const code = ext.exception?.code;

    return (
      ext?.code === 'GRPC_ERROR' &&
      code !== undefined &&
      (!blockedRetryListStatusNumbers || !blockedRetryListStatusNumbers.includes(code))
    );
  };

  return new ErrorLink(({ error }) => {
    if (!CombinedGraphQLErrors.is(error)) {
      return;
    }

    for (const err of error.errors) {
      if (!shouldRetry(err)) {
        continue;
      }

      return throwError(() => toGraphQLError(err));
    }
  });
};

// Athena's @authorize directive throws this exact message via ForbiddenError when the request
// reaches the gateway with no human profile attached to the session — i.e. the session cookie
// is missing or has been invalidated server-side. Treat it like UNAUTHENTICATED: send the user
// to login. We deliberately do NOT redirect on every FORBIDDEN, because legitimate
// permission-denied errors (authenticated user lacking access to a specific resource) also
// carry that code and would over-redirect.
const NO_PROFILE_MESSAGE = 'user profile is not available on context/session';

/**
 * Redirects unauthenticated errors to the login page.
 *
 * Throws UnauthenticatedError so that React error boundaries (CustomerBoundary) can react —
 * but ALSO triggers a hard navigation to login directly. The direct navigation is necessary
 * because some Apollo consumers discard the result (e.g. fire-and-forget mutations in
 * effects: `void mutate()`); their rejected promise never reaches a React boundary, so
 * relying on the boundary alone leaves those callers stuck on a broken page.
 */
export const redirectUnauthenticatedErrorsLink = () => {
  let hasRedirected = false;
  return new ErrorLink(({ error, operation }) => {
    if (!CombinedGraphQLErrors.is(error)) {
      return;
    }

    // The auth query itself handles UNAUTHENTICATED via auth context state — excluding it
    // here prevents a dual-redirect race between this link and the auth context.
    if (operation.operationName === 'UserSession') {
      return;
    }

    for (const err of error.errors) {
      if (
        !err.message.includes('16 UNAUTHENTICATED') &&
        !err.message.includes(NO_PROFILE_MESSAGE)
      ) {
        continue;
      }

      if (!hasRedirected) {
        hasRedirected = true;
        navigationHelpers.hardNavReplace(getLoginUrl(window.location.href), {
          label: 'login',
          data: { from: 'apollo-link', operationName: operation.operationName },
        });
      }
      return throwError(() => new UnauthenticatedError(operation.operationName, 'apollo-link'));
    }
  });
};

/**
 * Tracks the time it takes for the app to be downloaded, initialized, and to
 * trigger the first client-side GraphQL query.
 *
 * This lets us break down how much time is spent waiting for the app to get
 * ready before we start our render-blocking client-side data fetching
 * waterfall.
 *
 * NOTE: There isn't an easy way to configure this link to run *after* queries
 * are batched, so the timing tracked here will slightly underestimate the true
 * value.
 */
export const trackTimeToFirstQueryLink = (): ApolloLink =>
  new ApolloLink((operation, forward) => {
    try {
      const FIRST_CLIENT_QUERY_TRIGGERED_MARK = 'first-client-query-triggered';
      const TIME_TO_FIRST_CLIENT_QUERY_MEASURE = 'time-to-first-client-query';
      const TIME_TO_FIRST_CLIENT_QUERY_FROM_RESPONSE_START_MEASURE =
        'time-to-first-client-query-from-response-start';

      const { operationName } = operation;

      // We only want to emit this metric once per pageload
      if (performance.getEntriesByName(FIRST_CLIENT_QUERY_TRIGGERED_MARK)?.length) {
        return forward(operation);
      }

      performance.mark(FIRST_CLIENT_QUERY_TRIGGERED_MARK);
      const timeToFirstClientQuery = Math.round(
        performance.measure(TIME_TO_FIRST_CLIENT_QUERY_MEASURE, {
          end: FIRST_CLIENT_QUERY_TRIGGERED_MARK,
        }).duration,
      );

      // responseStart is triggered when we receive the first byte of the server's response
      const navigationEntries = performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[];
      const responseStart = navigationEntries?.[0]?.responseStart;
      let timeToFirstClientQueryFromResponseStart;
      if (responseStart) {
        timeToFirstClientQueryFromResponseStart = Math.round(
          performance.measure(TIME_TO_FIRST_CLIENT_QUERY_FROM_RESPONSE_START_MEASURE, {
            start: responseStart,
            end: FIRST_CLIENT_QUERY_TRIGGERED_MARK,
          }).duration,
        );
      }

      globalTrackEvent(
        'time-to-first-client-query',
        'cake-first-client-query-triggered',
        {
          data: {
            timeToFirstClientQuery,
            timeToFirstClientQueryFromResponseStart,
            operationName,
            percentOfTimeTabVisible:
              window?._opendoor?.tabVisibilityTracker?.getPercentOfTimeTabVisible(),
          },
        },
        {
          addTiming: true,
        },
      );

      return forward(operation);
    } catch (error) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#exceptions
      globalTrackError(
        'time-to-first-client-query-error',
        new Error(`Error tracking time-to-first-client-query for ${operation?.operationName}`),
        { error },
      );

      return forward(operation);
    }
  });

// all these types are needed because KeyFieldsFunction is no longer exported
type SafeReadonly<T> = T extends object ? Readonly<T> : T;

interface ReadFieldFunction {
  <V = StoreValue>(options: ReadFieldOptions): SafeReadonly<V> | undefined;
  <V = StoreValue>(fieldName: string, from?: StoreObject | Reference): SafeReadonly<V> | undefined;
}

type KeySpecifier = ReadonlyArray<string | KeySpecifier>;

/**
 * @internal
 */
export type KeyFieldsContext = {
  typename: string | undefined;
  storeObject: StoreObject;
  readField: ReadFieldFunction;
  selectionSet?: SelectionSetNode;
  fragmentMap?: Record<string, FragmentDefinitionNode>;
  keyObject?: Record<string, unknown>;
};

type KeyFieldsFunction = (
  object: Readonly<StoreObject>,
  context: KeyFieldsContext,
) => KeySpecifier | false | string | undefined;

const isValidKey = (value: StoreValue): value is string =>
  hasValue(value) && typeof value === 'string' && value.length > 0;

// Generates cache keys by checking for `uuid` and `token` fields
// in priority order. Delegates to the default handler after that,
// which handles `id` and `_id`.
export const dataIdFromObject: KeyFieldsFunction = (object, context) => {
  const { __typename, uuid, token } = object;
  if (typeof __typename === 'string') {
    if (isValidKey(uuid)) {
      return `${__typename}:${uuid}`;
    }
    if (isValidKey(token)) {
      return `${__typename}:${token}`;
    }
  }

  return defaultDataIdFromObject(object, context);
};
